import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {differenceInDay, parseDateWithFormat, subDate} from '@shared/dateUtils';
import {ApiResponseGetUbiResponse} from '@di-shared/api/di/__generated';
import {useSuspenseQuery} from '@tanstack/react-query';
import {ERROR} from '@di-shared/const';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

const STANDARD_OF_AGREE_DAYS = 90;

function useSafeDriving(vin: string) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const {data: ubiData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ubiController.getUbi4), vin],
    queryFn: () => diApi.ubiController.getUbi4({vin}),
    select: ({data}: ApiResponseGetUbiResponse) => {
      return {
        ...data,
        scoreDate: parseDateWithFormat(data.scoreDate),
        agreeDate: parseDateWithFormat(data.agreeDate),
      };
    },
  });

  let daysOfRecentlyTrips;
  let fromAgreeDate;
  const differenceDaysFromScoreDateToAgreeDate = !ubiData.scoreDate
    ? 0
    : !ubiData.agreeDate
      ? 0
      : differenceInDay(ubiData.scoreDate, ubiData.agreeDate, {includeReferenceDate: true});
  if (differenceDaysFromScoreDateToAgreeDate >= STANDARD_OF_AGREE_DAYS) {
    daysOfRecentlyTrips = STANDARD_OF_AGREE_DAYS;
    fromAgreeDate = ubiData.scoreDate ? CldrDatetimeFormatter.format(subDate(ubiData.scoreDate, {days: 89}), 'long') : `${ERROR.DASH}`;
  } else {
    daysOfRecentlyTrips = differenceDaysFromScoreDateToAgreeDate;
    fromAgreeDate = ubiData.agreeDate ? CldrDatetimeFormatter.format(ubiData.agreeDate, 'long') : `${ERROR.DASH}`;
  }

  const predictionScoreFromDistances = [
    {
      distance: 100,
      score: ubiData.safetyDrvScore100,
    },
    {
      distance: 300,
      score: ubiData.safetyDrvScore300,
    },
    {
      distance: 500,
      score: ubiData.safetyDrvScore500,
    },
    {
      distance: 1000,
      score: ubiData.safetyDrvScore1000,
    },
  ];

  return {
    ...ubiData,
    daysOfRecentlyTrips,
    fromAgreeDate,
    predictionScoreFromDistances,
  };
}

export default useSafeDriving;
